import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import ImageUpload from '../../common/ImageUpload';

const { FiX, FiUser, FiMail, FiPhone } = FiIcons;

const ContactModal = ({ isOpen, onClose, contact }) => {
  const { addContact, updateContact, contactTags, companies, customFields } = useData();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    tags: [],
    notes: '',
    avatar_url: '',
    company_id: '',
    custom_values: {}
  });

  useEffect(() => {
    if (contact) {
      setFormData({
        ...contact,
        custom_values: contact.custom_values || {}
      });
    } else {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        tags: [],
        notes: '',
        avatar_url: '',
        company_id: '',
        custom_values: {}
      });
    }
  }, [contact, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (contact) await updateContact(contact.id, formData);
    else await addContact(formData);
    onClose();
  };

  const handleCustomValueChange = (fieldId, value) => {
    setFormData({
      ...formData,
      custom_values: {
        ...formData.custom_values,
        [fieldId]: value
      }
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
              <SafeIcon icon={FiUser} className="text-blue-600" />
              {contact ? 'Edit Contact' : 'New Contact'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-slate-400" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-5 max-h-[75vh] overflow-y-auto custom-scrollbar">
            <ImageUpload value={formData.avatar_url} onChange={(url) => setFormData({ ...formData, avatar_url: url })} label="Contact Thumbnail" />
            
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Basic Info</label>
                <input placeholder="Full Name" required className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <input placeholder="Email Address" required type="email" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
              <input placeholder="Phone Number" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />

              {/* Dynamic Custom Fields */}
              {customFields.length > 0 && (
                <div className="pt-4 space-y-4 border-t border-slate-50">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Additional Fields</label>
                  {customFields.map(field => (
                    <div key={field.id} className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-500 pl-1">{field.label}</label>
                      <input 
                        type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                        className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold"
                        value={formData.custom_values[field.id] || ''}
                        onChange={e => handleCustomValueChange(field.id, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Link to Company</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold appearance-none" value={formData.company_id} onChange={e => setFormData({ ...formData, company_id: e.target.value })}>
                  <option value="">No Company</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>

              <div className="flex flex-wrap gap-2">
                {contactTags.map(tag => (
                  <button key={tag} type="button" onClick={() => setFormData(prev => ({ ...prev, tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag] }))} className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${formData.tags.includes(tag) ? 'bg-blue-600 text-white shadow-lg shadow-blue-100' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all">
              {contact ? 'Update Contact' : 'Create Contact'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ContactModal;